import { useTracking } from '@folklore/tracking';
import { animated, useSpring } from '@react-spring/web';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock-upgrade';
import classNames from 'classnames';
import { useEffect, useRef, useState, ReactNode, MouseEvent } from 'react';

import { MODAL_NAMESPACE } from '../../contexts/PopupsContext';
import PopupPortal from './PopupPortal';

import styles from '../../styles/popups/modal.module.css';

type ModalProps = {
    id: string;
    opened?: boolean;
    fullscreen?: boolean;
    position?: 'top' | 'bottom' | 'center';
    withBackdrop?: boolean;
    withTransition?: boolean;
    withScrollLock?: boolean;
    className?: string;
    innerClassName?: string;
    children?: ReactNode;
    requestClose?: () => void;
    onClickBackdrop?: (e: MouseEvent) => void;
    onOpened?: () => void;
    onClosed?: () => void;
};

function Modal({
    id,
    opened = true,
    position = 'center',
    fullscreen = false,
    withBackdrop = false,
    withTransition = false,
    withScrollLock = false,
    className = null,
    innerClassName = null,
    children = null,
    requestClose = null,
    onClickBackdrop = null,
    onOpened = null,
    onClosed = null,
}: ModalProps) {
    const [verticalPosition, horizontalPosition = verticalPosition] = position.split(' ');
    const [isTransitioning, setIsTransitioning] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const spring = useSpring({
        from: { y: opened ? '0%' : '0%', scale: opened ? 0.8 : 1, opacity: opened ? 0 : 1 },
        to: { y: opened ? '0%' : '0%', scale: opened ? 1 : 0.8, opacity: opened ? 1 : 0 },
        onStart: () => {
            setIsTransitioning(true);
        },
        onResolve: () => {
            if (!isTransitioning) {
                return;
            }
            setIsTransitioning(false);
            if (opened && withScrollLock) {
                disableBodyScroll(containerRef.current!);
            } else if (!opened && withScrollLock) {
                clearAllBodyScrollLocks();
            }
            if (opened && onOpened !== null) {
                onOpened();
            } else if (!opened && onClosed !== null) {
                onClosed();
            }
        },
    });

    useEffect(() => {
        if (withTransition) {
            return;
        }
        if (opened && withScrollLock) {
            disableBodyScroll(containerRef.current!);
        } else if (!opened && withScrollLock) {
            clearAllBodyScrollLocks();
        }
        if (opened && onOpened !== null) {
            onOpened();
        } else if (!opened && onClosed !== null) {
            onClosed();
        }
    }, [opened, onOpened, onClosed, withTransition]);

    useEffect(
        () => () => {
            if (withScrollLock) {
                clearAllBodyScrollLocks();
            }
        },
        [withScrollLock],
    );

    const tracking = useTracking();
    useEffect(() => {
        tracking.trackModal(id, opened ? 'open' : 'close');
    }, [id, opened]);

    const onClickModal = (e) => {
        if (e.target === containerRef.current && onClickBackdrop !== null) {
            onClickBackdrop(e);
        }
    };

    return (
        <PopupPortal id={id} requestClose={requestClose} namespace={MODAL_NAMESPACE}>
            <animated.div
                className={classNames([
                    styles.container,
                    {
                        [styles.fullscreen]: fullscreen,
                        [styles.withBackdrop]: withBackdrop,
                        [styles.isTransitioning]: withTransition && isTransitioning,
                        [styles[verticalPosition]]: verticalPosition !== null,
                        [styles[horizontalPosition]]: horizontalPosition !== null,
                    },
                    className,
                ])}
                data-nosnippet
                onClick={withBackdrop ? onClickModal : null}
                style={{
                    background: spring.opacity.to((o) =>
                        withBackdrop ? `rgba(0, 0, 0, ${0.5 * o})` : 'transparent',
                    ),
                }}
                ref={containerRef}
            >
                <animated.div
                    style={withTransition ? spring : undefined}
                    className={classNames([styles.inner, innerClassName])}
                    tabIndex={-1}
                >
                    {children}
                </animated.div>
            </animated.div>
        </PopupPortal>
    );
}

export default Modal;
